import styled from '@emotion/styled';
import {
	GridItem,
	ITheme,
	Search,
	Select,
	StyledIconButton,
	StyledInput,
	StyledSelectMain,
} from '../../../../ui';
import React, { useEffect, useState } from 'react';
import {
	getOverlapWithHeaderStyles,
	SearchBarWrap,
	SolidContentWrap,
	StyledHeader,
} from '../../../shared/components';
import { useTranslation } from '../../../shared/i18n/translate/useTranslation';
import Responsive from '../../../../ui/utils/Responsive';
import { getLocaleFromPath } from '../../../shared/i18n/locale/getLocaleFromPath';
import { useRouter } from 'next/router';
import { theme } from '../../../shared/styles';
import {
	getSessionStorageItem,
	setSessionStorageItem,
} from '../../../shared/utils/sessionStorage';
import {
	ALL_CATEGORIES,
	ContentCategory,
} from '../../../shared/domain/utils/getContentCategories';
import {
	ALL_YEARS,
	YearFilterValue,
} from '../../domain/hooks/useAllYearsFilters';

export interface ISearchParams {
	query: string;
	year: YearFilterValue;
	category?: ContentCategory;
}

interface ISearchBarProps {
	disabled?: boolean;
	years: YearFilterValue[];
	categories?: ContentCategory[];
	onSearch: (searchParams: ISearchParams) => void;
}

export const SearchBar = ({
	years,
	disabled,
	categories,
	onSearch,
}: ISearchBarProps) => {
	const { t } = useTranslation();
	const { asPath } = useRouter();
	const isSpanishLocale = getLocaleFromPath(asPath)?.prefix === 'es';

	const sectionType = asPath.includes('/press-releases')
		? 'news-press-releases'
		: asPath.includes('/blogs')
		? 'news-blogs'
		: 'news-stories';

	const [selectedCategoryId, setSelectedCategoryId] =
		useState<string>(ALL_CATEGORIES);
	const selectedCategory =
		categories?.find((c) => c.id === selectedCategoryId) ||
		(categories && categories[0]);

	const [year, setYear] = useState(years[0]);

	// Read stored filters from sessionStorage after hydration to avoid mismatch
	useEffect(() => {
		const storedCategoryId = getSessionStorageItem(
			`${sectionType}-category-filter`,
		);
		const storedYear = getSessionStorageItem(`${sectionType}-year-filter`);

		const newCategoryId =
			storedCategoryId !== selectedCategoryId ? storedCategoryId : null;
		const newYear = storedYear && storedYear !== year ? storedYear : null;

		if (newCategoryId) {
			setSelectedCategoryId(newCategoryId);
		}
		if (newYear) {
			setYear(newYear);
		}

		if (newCategoryId || newYear) {
			const category = categories?.find(
				(c) => c.id === (newCategoryId || selectedCategoryId),
			);
			onSearch({ query: '', year: newYear || year, category });
		}
	}, [sectionType]);
	const [query, setQuery] = useState('');

	const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		onSearch({ query, year, category: selectedCategory });
	};

	const onCategoryChange = (categoryId: string) => {
		setSelectedCategoryId(categoryId);
		setSessionStorageItem(`${sectionType}-category-filter`, categoryId);
		const category = categories?.find((c) => c.id === categoryId);

		onSearch({ query, year, category });
	};

	const onYearChange = (value: string) => {
		setYear(value);
		setSessionStorageItem(`${sectionType}-year-filter`, value);
		onSearch({ query, year: value, category: selectedCategory });
	};

	const columnCount = isSpanishLocale
		? categories
			? 6
			: 9
		: categories
		? 8
		: 10;

	return (
		<StyledSolidContentWrap>
			<form onSubmit={(e) => onSubmit(e)}>
				<StyledSelectWrap>
					{years && (
						<GridItem columns={[isSpanishLocale ? 3 : 2, { tablet: 12 }]}>
							<label id="year-select-label" htmlFor="year-select">
								{t('news_searchbar_year')}
							</label>
							<Select
								id="year-select"
								ariaLabelledBy="year-select-label"
								items={years}
								itemToString={(value) =>
									value === ALL_YEARS
										? t('news_searchbar_all_years')
										: (value as string | undefined) || ''
								}
								onChange={(value) => onYearChange(value as string)}
								initialSelectedItem={year}
								selectedItem={year}
							/>
						</GridItem>
					)}

					{categories && (
						<GridItem columns={[3, { tablet: 12 }]}>
							<label id="category-select-label" htmlFor="category-select">
								{t('news_searchbar_category')}
							</label>
							<Select
								id="category-select"
								ariaLabelledBy="category-select-label"
								items={categories.map((category) => category.id)}
								itemToString={(category) =>
									categories.find((c) => c.id === category)?.title || ''
								}
								onChange={(categoryId) =>
									onCategoryChange(categoryId as string)
								}
								initialSelectedItem={selectedCategoryId}
								selectedItem={selectedCategoryId}
							/>
						</GridItem>
					)}
					<GridItem columns={[columnCount, { tablet: 12 }]}>
						<label htmlFor="search-input">{t('news_searchbar_query')}</label>
						<Search
							autoComplete="off"
							isForm
							id="search-input"
							disabled={disabled}
							onUpdateQuery={(value) => setQuery(value)}
							placeholder={t('news_searchbar_query_placeholder')}
						/>
					</GridItem>
				</StyledSelectWrap>
			</form>
		</StyledSolidContentWrap>
	);
};

const StyledSolidContentWrap = styled(SolidContentWrap)<{}, ITheme>`
	border-radius: 8px;
	padding: 24px;
	box-shadow: 0px 8px 20px 0px #0000001a;

	${StyledHeader} + style + &,
      ${StyledHeader} + & {
		${getOverlapWithHeaderStyles()}
		@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
			padding: 16px;
			margin: calc(-27% + 16px) 16px 40px;
		}
	}

	@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
		margin-bottom: 32px;
	}
`;

const StyledSelectWrap = styled(SearchBarWrap)<{}, ITheme>`
	margin-bottom: 0;
	flex-wrap: nowrap;

	@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
		flex-wrap: wrap;
	}

	${GridItem} {
		> * {
			margin-top: 3px;
		}
	}

	${StyledSelectMain} {
		&:focus-visible {
			outline: ${theme.button?.primary?.focus?.outline};
		}
	}

	${StyledInput} {
		&:focus-visible {
			outline: ${theme.button?.primary?.focus?.outline};
			border-radius: 4px;
		}
	}

	${StyledIconButton} {
		&:hover,
		&:active {
			box-shadow: none !important;
			border: none !important;
		}

		&:focus-visible {
			outline: ${theme.button?.primary?.focus?.outline};
			z-index: 1;
		}
	}
`;
